'use client';

import { useCallback, useState } from 'react';

import { presignAdvisorCsvUpload } from '../../actions';

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export type UseCsvUploadResult = {
  status: UploadStatus;
  errorMessage: string | null;
  upload: (file: File) => Promise<void>;
  reset: () => void;
};

const MAX_BYTES_CLIENT_LIMIT = 31_457_280;

function mapErrorCode(code: string): string {
  switch (code) {
    case 'UNAUTHORIZED':
      return 'You must be logged in to upload.';
    case 'INVALID_INPUT':
      return 'Invalid file. Please select a CSV.';
    case 'FILE_TOO_LARGE':
      return 'File too large. Maximum size is 30 MB.';
    case 'INTERNAL':
      return 'Upload service unavailable. Try again later.';
    default:
      return 'Upload failed. Please try again.';
  }
}

export function useCsvUpload(): UseCsvUploadResult {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const upload = useCallback(async (file: File): Promise<void> => {
    const isCsvByExtension = file.name.toLowerCase().endsWith('.csv');
    const isCsvByMime = file.type === 'text/csv';
    if (!isCsvByExtension && !isCsvByMime) {
      setStatus('error');
      setErrorMessage('Only .csv files are allowed');
      return;
    }

    if (file.size <= 0) {
      setStatus('error');
      setErrorMessage('File is empty. Please select a valid CSV.');
      return;
    }

    if (file.size > MAX_BYTES_CLIENT_LIMIT) {
      setStatus('error');
      setErrorMessage('File too large. Maximum size is 30 MB.');
      return;
    }

    setStatus('uploading');
    setErrorMessage(null);

    try {
      const requestBody: Parameters<typeof presignAdvisorCsvUpload>[0] = {
        filename: file.name,
        contentType: 'text/csv',
        size: file.size,
      };
      const presignResult = await presignAdvisorCsvUpload(requestBody);

      if (!presignResult.ok) {
        setErrorMessage(mapErrorCode(presignResult.code));
        setStatus('error');
        return;
      }

      const putResponse = await fetch(presignResult.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'text/csv' },
      });

      if (!putResponse.ok) {
        setErrorMessage('Upload to storage failed');
        setStatus('error');
        return;
      }

      setStatus('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setErrorMessage(`Network error: ${message}`);
      setStatus('error');
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setErrorMessage(null);
  }, []);

  return { status, errorMessage, upload, reset };
}
